import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, X, Tag } from "lucide-react";
import { toast } from "sonner";

interface TitleManagerProps {
  churchId?: string;
}

export type OrgTitle = { id: string; name: string; church_id: string };

export const BUILT_IN_TITLES = [
  "Rev. Dr.",
  "Rev.",
  "Dr.",
  "Pastor",
  "President",
  "Vice-President",
  "Chairman",
  "Vice-Chairman",
  "Treasurer",
  "Assistant Treasurer",
  "Secretary",
  "Assistant Secretary",
] as const;

const normalizeTitle = (value: string) => value.trim().toLowerCase();

export function useTitles(churchId?: string) {
  return useQuery({
    queryKey: ["organization_titles", churchId],
    enabled: !!churchId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organization_titles")
        .select("*")
        .eq("church_id", churchId!)
        .order("name");
      if (error) throw error;
      return data as OrgTitle[];
    },
  });
}

export default function TitleManager({ churchId }: TitleManagerProps) {
  const [newTitle, setNewTitle] = useState("");
  const [isSeeding, setIsSeeding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<OrgTitle | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const queryClient = useQueryClient();
  const { data: titles = [], isLoading } = useTitles(churchId);

  const protectedTitleNames = useMemo(
    () => new Set(BUILT_IN_TITLES.map(normalizeTitle)),
    []
  );

  const missingBuiltIns = useMemo(() => {
    const existing = new Set(titles.map((t) => normalizeTitle(t.name)));
    return BUILT_IN_TITLES.filter((title) => !existing.has(normalizeTitle(title)));
  }, [titles]);

  const builtInTitles = useMemo(() => {
    const titleMap = new Map(titles.map((t) => [normalizeTitle(t.name), t]));
    return BUILT_IN_TITLES
      .map((name) => titleMap.get(normalizeTitle(name)))
      .filter(Boolean) as OrgTitle[];
  }, [titles]);

  const customTitles = useMemo(
    () => titles
      .filter((t) => !protectedTitleNames.has(normalizeTitle(t.name)))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" })),
    [titles, protectedTitleNames]
  );

  useEffect(() => {
    if (!churchId || isLoading || isSeeding || missingBuiltIns.length === 0) return;

    let cancelled = false;
    const seedBuiltIns = async () => {
      setIsSeeding(true);
      const { error } = await supabase
        .from("organization_titles")
        .insert(missingBuiltIns.map((name) => ({ name, church_id: churchId })));

      if (!cancelled) {
        setIsSeeding(false);
        if (error && !error.message.toLowerCase().includes("duplicate")) {
          toast.error(error.message);
          return;
        }
        queryClient.invalidateQueries({ queryKey: ["organization_titles"] });
      }
    };

    seedBuiltIns();
    return () => {
      cancelled = true;
    };
  }, [churchId, isLoading, isSeeding, missingBuiltIns, queryClient]);

  const addMutation = useMutation({
    mutationFn: async (name: string) => {
      const { error } = await supabase
        .from("organization_titles")
        .insert({ name: name.trim(), church_id: churchId! });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization_titles"] });
      setNewTitle("");
      
    },
    onError: (err: Error) => {
      if (err.message.includes("duplicate")) toast.error("Title already exists");
      else toast.error(err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      if (protectedTitleNames.has(normalizeTitle(name))) {
        throw new Error("This is a built-in title and cannot be removed");
      }
      const { error } = await supabase
        .from("organization_titles")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization_titles"] });
      
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Tag className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium text-foreground">Titles</span>
        <span className="text-xs text-muted-foreground">(e.g. President, Secretary)</span>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {isLoading && !isSeeding ? (
          <span className="text-xs text-muted-foreground">Loading...</span>
        ) : titles.length === 0 && !isSeeding ? (
          <span className="text-xs text-muted-foreground italic">No titles yet — add one below</span>
        ) : (
          <div className="w-full space-y-3">
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Built-in Titles</p>
              <div className="flex flex-wrap gap-1.5">
                {builtInTitles.map((t) => (
                  <Badge key={t.id} variant="secondary" className="text-xs">
                    {t.name}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Custom Titles</p>
              <div className="flex flex-wrap gap-1.5">
                {customTitles.length === 0 ? (
                  <span className="text-xs text-muted-foreground italic">No custom titles yet</span>
                ) : (
                  customTitles.map((t) => (
                    <Badge key={t.id} variant="secondary" className="text-xs gap-1 pr-1">
                      {t.name}
                      <button
                        type="button"
                        onClick={() => {
                          setDeleteTarget(t);
                          setDeleteConfirmText("");
                        }}
                        className="hover:text-destructive ml-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="New title name..."
          className="h-8 text-sm flex-1"
          onKeyDown={(e) => {
            if (e.key === "Enter" && newTitle.trim()) {
              e.preventDefault();
              addMutation.mutate(newTitle);
            }
          }}
        />
        <Button
          className="h-9 px-3"
          disabled={!newTitle.trim() || addMutation.isPending}
          onClick={() => addMutation.mutate(newTitle)}
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add
        </Button>
      </div>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-destructive">Delete Title</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This will permanently delete <span className="font-semibold text-foreground">"{deleteTarget?.name}"</span>. This action cannot be undone.
            </p>
            <div className="space-y-2">
              <Label>Type <span className="font-semibold text-foreground">"{deleteTarget?.name}"</span> to confirm</Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={deleteTarget?.name}
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button
                variant="destructive"
                disabled={deleteConfirmText !== deleteTarget?.name || deleteMutation.isPending}
                onClick={() => {
                  if (deleteTarget && deleteConfirmText === deleteTarget.name) {
                    deleteMutation.mutate(
                      { id: deleteTarget.id, name: deleteTarget.name },
                      { onSuccess: () => setDeleteTarget(null) }
                    );
                  }
                }}
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
